import React, { useMemo } from 'react';
import { BarChart2 } from 'lucide-react';
import { PreviewCardShell } from './PreviewCardShell';
import { BarChart } from './charts/BarChart';
import { GroupedBarChart } from './charts/GroupedBarChart';
import { PieChart } from './charts/PieChart';
import {
  parseChartData,
  detectColorScheme,
  ChartType
} from './charts/chartUtils';

interface ChartMockupProps {
  chartType: ChartType;
  data: string; // JSON array string
  title?: string;
  previewLabel?: string;
}

export const ChartMockup: React.FC<ChartMockupProps> = ({
  chartType,
  data,
  title,
  previewLabel = 'Chart Preview'
}) => {
  // Parse and validate data
  const parsedData = useMemo(() => parseChartData(data), [data]);

  // Detect color scheme
  const colorScheme = useMemo(() => detectColorScheme(parsedData), [parsedData]);

  // Error state for malformed data
  if (data && data.trim() !== '' && parsedData.length === 0) {
    return (
      <PreviewCardShell
        className="mb-3"
        bodyClassName="p-4"
        badgeClassName="bg-gradient-to-r from-purple-400 to-purple-300"
        badge={<BarChart2 className="h-4 w-4 text-white" />}
        label={previewLabel}
      >
        <div className="flex items-center justify-center h-32 text-red-500 dark:text-red-400">
          <p className="text-sm">Unable to parse chart data</p>
        </div>
      </PreviewCardShell>
    );
  }

  // Render appropriate chart based on type
  const renderChart = () => {
    switch (chartType) {
      case 'grouped_bar':
        return <GroupedBarChart data={parsedData} title={title} />;

      case 'pie':
        return <PieChart data={parsedData} colorScheme={colorScheme} title={title} />;

      case 'bar':
      default:
        return <BarChart data={parsedData} colorScheme={colorScheme} title={title} />;
    }
  };

  return (
    <PreviewCardShell
      className="mb-3"
      bodyClassName="p-4"
      badgeClassName="bg-gradient-to-r from-purple-400 to-purple-300"
      badge={<BarChart2 className="h-4 w-4 text-white" />}
      label={previewLabel}
    >
      {renderChart()}
    </PreviewCardShell>
  );
};
